import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormGroup,
  Divider,
} from "@mui/material";
import DrawIcon from "@mui/icons-material/Draw";
import { PencilBrush } from "fabric";
import { useData } from "../hooks/useData";
import useCanvasHistory from "../hooks/useCanvasHistory"; // Import for saving history

const DrawingToolbar = ({ canvas }) => {
  const { setSnackbar, selectedObject, setSelectedObject } = useData();
  const { saveHistory } = useCanvasHistory(canvas); // Hook for saving canvas history
  const [drawingStroke, setDrawingStroke] = useState(1);
  const [drawingMode, setDrawingMode] = useState(false);
  const [drawingLabel, setDrawingLabel] = useState("Start Drawing");

  // Sync drawingStroke with selected path's strokeWidth
  useEffect(() => {
    if (selectedObject && selectedObject.type === "path") {
      const strokeWidth = selectedObject.strokeWidth || 1;
      setDrawingStroke(strokeWidth);
      console.log("DrawingToolbar: Synced stroke width", {
        strokeWidth,
        path: selectedObject,
      });

      // Ensure the object is selected on the canvas
      const activeObject = canvas.getActiveObject();
      if (activeObject !== selectedObject) {
        canvas.setActiveObject(selectedObject);
        canvas.requestRenderAll();
      }
    }
  }, [selectedObject, canvas]);

  // Validate that the selectedObject is a path and still on the canvas
  const validatePath = () => {
    if (!selectedObject || selectedObject.type !== "path") {
      return false; // No path selected
    }

    // Check if the object is still on the canvas
    const canvasObjects = canvas.getObjects();
    if (!canvasObjects.includes(selectedObject)) {
      setSnackbar({
        open: true,
        message: "The selected path is no longer on the canvas.",
        severity: "error",
      });
      setSelectedObject(null);
      return false;
    }

    return true;
  };

  // Handle stroke width change for selected path or drawing brush
  const handleStrokeChange = (e) => {
    const newStrokeWidth = Math.max(1, parseInt(e.target.value) || 1);
    setDrawingStroke(newStrokeWidth);

    if (selectedObject && validatePath()) {
      try {
        selectedObject.set({ strokeWidth: newStrokeWidth });
        canvas.requestRenderAll();
        canvas.fire("object:modified", { target: selectedObject });
        saveHistory(canvas);
        console.log("DrawingToolbar: Updated path stroke width", {
          strokeWidth: newStrokeWidth,
          path: selectedObject,
        });
      } catch (error) {
        console.error(
          "DrawingToolbar: Failed to update path stroke width",
          error
        );
        setSnackbar({
          open: true,
          message: "Error updating stroke width. Please try again.",
          severity: "error",
        });
      }
    }

    // Update brush width if in drawing mode
    if (drawingMode && canvas.freeDrawingBrush) {
      canvas.freeDrawingBrush.width = newStrokeWidth;
      console.log("DrawingToolbar: Updated brush width", {
        brushWidth: newStrokeWidth,
      });
    }
  };

  const initializeDrawing = useCallback(() => {
    if (!canvas) {
      setSnackbar({
        open: true,
        message: "Canvas not initialized.",
        severity: "error",
      });
      return;
    }

    console.log("DrawingToolbar: Checking PencilBrush", {
      PencilBrush: PencilBrush,
    });
    if (!PencilBrush) {
      console.error("DrawingToolbar: PencilBrush is not defined");
      setSnackbar({
        open: true,
        message: "Failed to initialize drawing. Please try refreshing.",
        severity: "error",
      });
      return;
    }

    try {
      canvas.isDrawingMode = true;
      canvas.freeDrawingBrush = new PencilBrush(canvas);
      canvas.freeDrawingBrush.color = "blue";
      canvas.freeDrawingBrush.width = parseInt(drawingStroke) || 1;
      canvas.requestRenderAll();
      console.log("DrawingToolbar: Initialized drawing mode");
    } catch (error) {
      console.error("DrawingToolbar: Failed to initialize drawing", error);
      setSnackbar({
        open: true,
        message: "Error starting drawing mode. Please try again.",
        severity: "error",
      });
    }
  }, [canvas, drawingStroke, setSnackbar]);

  useEffect(() => {
    if (canvas) {
      // Clean up previous listeners
      canvas.off("mouse:down");
      if (drawingMode) {
        initializeDrawing();
      }
      return () => {
        canvas.off("mouse:down");
      };
    }
  }, [canvas, drawingMode, drawingStroke, initializeDrawing]);

  const toggleDrawingMode = () => {
    if (!canvas) {
      setSnackbar({
        open: true,
        message: "Canvas not initialized.",
        severity: "error",
      });
      return;
    }

    try {
      if (drawingMode) {
        setDrawingMode(false);
        setDrawingLabel("Start Drawing");
        canvas.isDrawingMode = false;
        console.log("DrawingToolbar: Stopped drawing mode");
      } else {
        setDrawingMode(true);
        setDrawingLabel("Stop Drawing");
        initializeDrawing();
      }
    } catch (error) {
      console.error("DrawingToolbar: Failed to toggle drawing mode", error);
      setSnackbar({
        open: true,
        message: "Error toggling drawing mode. Please try again.",
        severity: "error",
      });
    }
  };

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          color: "#0d2445",
          fontFamily: "Roboto",
          fontWeight: 700,
          textAlign: "left",
        }}
      >
        Draw Freehand
      </Typography>
      <Typography
        variant="body1"
        sx={{
          marginTop: "8px",
          color: "#5e5e5e",
          textAlign: "left",
        }}
      >
        Click "Start Drawing" and draw on the canvas. Click "Stop Drawing" when
        finished. Select a path to adjust its stroke width.
      </Typography>
      <Divider
        sx={{
          marginTop: "12px",
          marginBottom: "12px",
          borderWidth: 1,
          borderColor: "grey",
          borderLeft: 0,
          borderRight: 0,
          borderBottom: 0,
        }}
        variant="fullWidth"
        orientation="horizontal"
      />
      <Box>
        <FormGroup>
          <TextField
            fullWidth
            type="number"
            id="drawStroke"
            margin="normal"
            InputProps={{
              sx: { bgcolor: "#fff" },
              endAdornment: <DrawIcon />,
              inputProps: { min: 1 }, // Enforce minimum stroke width
            }}
            label="Brush Width"
            value={drawingStroke}
            onChange={handleStrokeChange} // Use new handler
            variant="outlined"
          />
          <Button
            variant="contained"
            onClick={toggleDrawingMode}
            sx={{ mt: 1 }}
          >
            {drawingLabel}
          </Button>
        </FormGroup>
      </Box>
    </Box>
  );
};

export default DrawingToolbar;
